/**
 * Patient-portal access menu — the chart's top-right entry to the invite
 * ceremony (backend: disease_mgmt atman/portal/invites.py, SUBSCRIPTION_ADR D1).
 *
 * One icon button, one menu, three states fetched live on open:
 *   not linked  → "Invite to patient portal" (mints a single-use, short-TTL
 *                 invite; supersedes any outstanding one)
 *   pending     → shows expiry, offers re-invite (supersede)
 *   linked      → shows since-when + method, offers "Revoke portal access"
 *                 (revoke, never delete — the patient's next portal request
 *                 denies; re-access is a fresh invite)
 *
 * In local/qa the mint response carries the raw invite code and it is shown
 * here to hand to the patient; in production the response is metadata-only —
 * delivery to the on-file email is the pending notification build, and this
 * dialog says so instead of pretending.
 */
import React, { useState } from 'react'
import {
  Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, ListSubheader, Menu, MenuItem, TextField, Tooltip, Typography,
} from '@mui/material'
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined'
import moment from 'moment'
import { toast } from 'react-toastify'
import { PatientService } from '../../../../services/patient.service'
import Helper from '../../../../utils/helper'

interface PortalStatus {
  linked: boolean
  binding: { bound_on: number; binding_method: string; provider: string } | null
  latest_invite: {
    status: string; expires_on: number; expired: boolean;
    created_on: number; created_by: string | null
  } | null
}

interface IProps {
  patientRefId: string
}

const patientService = new PatientService()

const PortalInviteMenu: React.FC<IProps> = ({ patientRefId }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [status, setStatus] = useState<PortalStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [inviteCode, setInviteCode] = useState<string | null>(null)
  const [inviteExpiry, setInviteExpiry] = useState<number | null>(null)
  const [confirmRevoke, setConfirmRevoke] = useState(false)

  const loadStatus = async () => {
    setLoading(true)
    try {
      const res = await patientService.getPortalStatus(patientRefId)
      setStatus(res.data?.data ?? null)
    } catch (err) {
      console.error('portal status load failed:', err)
      setStatus(null)
    } finally {
      setLoading(false)
    }
  }

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget)
    loadStatus()
  }
  const handleClose = () => setAnchorEl(null)

  const handleInvite = async () => {
    handleClose()
    try {
      const res = await patientService.createPortalInvite(patientRefId)
      const data = res.data?.data
      toast.success('Portal invite created', Helper.bottom_center())
      // Raw code is present in local/qa only; production delivery is the
      // notification build (emailed link). Show what we honestly have.
      setInviteCode(data?.invite_token ?? null)
      setInviteExpiry(data?.expires_on ?? null)
    } catch (err: any) {
      const message = err?.response?.data?.error?.message
        ?? 'Could not create the invite.'
      toast.error(message, Helper.bottom_center())
    }
  }

  const handleRevoke = async () => {
    setConfirmRevoke(false)
    try {
      await patientService.revokePortalIdentity(patientRefId)
      toast.success('Portal access revoked', Helper.bottom_center())
    } catch (err: any) {
      const message = err?.response?.data?.error?.message
        ?? 'Could not revoke portal access.'
      toast.error(message, Helper.bottom_center())
    }
  }

  const copyCode = async () => {
    if (inviteCode) {
      await navigator.clipboard.writeText(inviteCode)
      toast.success('Invite code copied', Helper.bottom_center())
    }
  }

  const statusLine = () => {
    if (loading) return 'Checking…'
    if (!status) return 'Status unavailable'
    if (status.linked && status.binding) {
      return `Linked since ${moment.unix(status.binding.bound_on).format('MMM DD, YYYY')}`
    }
    const invite = status.latest_invite
    if (invite && invite.status === 'pending' && !invite.expired) {
      return `Invite pending · expires ${moment.unix(invite.expires_on).format('MMM DD')}`
    }
    return 'Not linked'
  }

  const linked = Boolean(status?.linked)

  return (
    <>
      <Tooltip title="Patient portal access" placement="bottom">
        <IconButton size="small" onClick={handleOpen} sx={{ marginTop: '0.4rem' }}>
          <ManageAccountsOutlinedIcon
            fontSize="small"
            sx={{ color: 'var(--color-text-secondary)' }}
          />
        </IconButton>
      </Tooltip>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <ListSubheader sx={{ lineHeight: '2rem', fontSize: '0.72rem',
                             backgroundColor: 'transparent' }}>
          {loading ? <CircularProgress size={12} sx={{ mr: 1 }} /> : null}
          Portal: {statusLine()}
        </ListSubheader>
        {!linked && (
          <MenuItem onClick={handleInvite} sx={{ fontSize: '0.8rem' }}>
            Invite to patient portal
          </MenuItem>
        )}
        {!linked && status?.latest_invite?.status === 'pending' && (
          <MenuItem onClick={handleInvite} sx={{ fontSize: '0.8rem' }}>
            Re-invite (replaces pending invite)
          </MenuItem>
        )}
        {linked && (
          <MenuItem onClick={() => { handleClose(); setConfirmRevoke(true) }}
                    sx={{ fontSize: '0.8rem', color: 'var(--color-primary)' }}>
            Revoke portal access
          </MenuItem>
        )}
      </Menu>

      <Dialog open={inviteCode !== null || inviteExpiry !== null}
              onClose={() => { setInviteCode(null); setInviteExpiry(null) }}
              maxWidth="xs" fullWidth>
        <DialogTitle>Portal invite created</DialogTitle>
        <DialogContent>
          {inviteCode ? (
            <>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Single-use code{inviteExpiry
                  ? `, expires ${moment.unix(inviteExpiry).format('MMM DD, YYYY h:mm a')}`
                  : ''}. The patient signs in at the portal and enters it under
                "Link my record". (Emailed link delivery arrives with the
                notification build.)
              </Typography>
              <TextField value={inviteCode} fullWidth size="small"
                         InputProps={{ readOnly: true }} />
            </>
          ) : (
            <Typography variant="body2">
              Invite created{inviteExpiry
                ? `; expires ${moment.unix(inviteExpiry).format('MMM DD, YYYY')}`
                : ''}. Delivery to the patient's on-file email is not wired
              yet — the code is only surfaced directly in local/qa
              environments.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {inviteCode && (
            <Button onClick={copyCode} variant="contained" disableElevation>
              Copy code
            </Button>
          )}
          <Button onClick={() => { setInviteCode(null); setInviteExpiry(null) }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmRevoke} onClose={() => setConfirmRevoke(false)}
              maxWidth="xs" fullWidth>
        <DialogTitle>Revoke portal access?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The patient's sign-in stops resolving to this chart immediately.
            Nothing is deleted — the binding is revoked with its audit trail,
            and re-access requires a fresh invite.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRevoke(false)}>Cancel</Button>
          <Button onClick={handleRevoke} variant="contained" disableElevation>
            Revoke
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default PortalInviteMenu
